import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink } from '../components/Nav';
import { CheckIcon } from '../components/icons';
import { Button, ErrorText, Input, Spinner } from '../components/ui';
import { track } from '../lib/amplitude';
import { ApiError, errorMessage } from '../lib/api';
import { isFramed } from '../lib/framing';
import { decideDeviceCode, fetchDeviceCode } from '../queries/device';

/**
 * Authorizing Slate for Mac.
 *
 * The desktop app never receives a credential through a URL scheme, because any
 * app on the machine can claim slate:// and intercept one. Instead it shows a
 * code, the person confirms that code here while signed in, and the app
 * collects its own token over a connection it opened itself.
 *
 * Which means the job of this page is narrow and important: show the human
 * exactly what they are about to let in, and make saying no as easy as saying
 * yes.
 */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function normalise(raw: string): string {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return cleaned.length > 4 ? `${cleaned.slice(0, 4)}-${cleaned.slice(4, 8)}` : cleaned;
}

export function DevicePage({ code: initialCode }: { code?: string }) {
  const { user, loading } = useAuth();

  const [code, setCode] = useState(initialCode ? normalise(initialCode) : '');
  const [decided, setDecided] = useState<'approve' | 'deny' | null>(null);

  const ready = code.length === 9;

  const details = useQuery({
    queryKey: ['device-code', code],
    queryFn: () => fetchDeviceCode(code),
    enabled: ready && Boolean(user),
    retry: false,
  });

  const decide = useMutation({
    mutationFn: (decision: 'approve' | 'deny') => decideDeviceCode(code, decision),
    onSuccess: (_data, decision) => {
      track(decision === 'approve' ? 'Device Authorized' : 'Device Denied');
      setDecided(decision);
    },
  });

  // Never inside somebody else's page: the frame around this one decides what
  // the person thinks they are approving. The edge sends frame-ancestors for
  // the same reason; this is the half that does not depend on a header
  // arriving. Below every hook, so the order of hooks never changes.
  if (isFramed()) {
    return (
      <Shell>
        <p className="text-sm text-slate-300">
          Open this page directly at{' '}
          <span className="font-medium text-slate-100">app.slatevideo.com/device</span> to authorize a
          device. It will not run inside another site.
        </p>
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
          <Spinner />
          <span className="text-body">Checking your session…</span>
        </div>
      </Shell>
    );
  }

  if (!user) {
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">Sign in to connect the desktop app</h1>
        <p className="mt-2 text-body text-chalk-dim">
          Slate for Mac is asking to sign in as you. Sign in here first, then come back to this
          page. The code in the app keeps working for a few minutes.
        </p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <AppLink to="/login" className="sm:flex-1">
            <Button variant="primary" size="lg" className="w-full">
              Sign in
            </Button>
          </AppLink>
          <AppLink to="/signup" className="sm:flex-1">
            <Button size="lg" className="w-full">
              Create an account
            </Button>
          </AppLink>
        </div>
      </Shell>
    );
  }

  if (decided === 'approve') {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-go">
          <CheckIcon size={18} />
          <span className="text-caption uppercase tracking-[0.08em]">Connected</span>
        </div>
        <h1 className="mt-2 text-h3 text-chalk">Slate for Mac is signed in</h1>
        <p className="mt-2 text-body text-chalk-dim">
          You can close this tab and go back to the app. Recordings will land in your library.
        </p>
        <p className="mt-4 text-caption text-chalk-faint">
          You can disconnect this Mac at any time from Settings, and it stops working immediately.
        </p>
      </Shell>
    );
  }

  if (decided === 'deny') {
    return (
      <Shell>
        <h1 className="text-h3 text-chalk">Nothing was connected</h1>
        <p className="mt-2 text-body text-chalk-dim">
          The request was turned down and the code is now dead. If that was not you, no action is
          needed: nothing was granted.
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <span className="text-caption uppercase tracking-[0.08em] text-chalk-faint">
        Connect a device
      </span>
      <h1 className="mt-2 text-h3 text-chalk">Enter the code from the app</h1>
      <p className="mt-2 text-body text-chalk-dim">
        The app shows an eight character code. Type it here to sign that copy of it in as{' '}
        <span className="text-chalk">{user.email}</span>.
      </p>

      <div className="mt-5">
        <Input
          label="Code"
          value={code}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          placeholder="ABCD-EFGH"
          onChange={(event) => setCode(normalise(event.target.value))}
          className="font-mono tracking-[0.2em]"
        />
      </div>

      {ready && details.isError ? (
        <div className="mt-3">
          <ErrorText>
            {details.error instanceof ApiError && details.error.isNotFound
              ? 'That code is not valid, or it has expired. Ask the app for a new one.'
              : errorMessage(details.error)}
          </ErrorText>
        </div>
      ) : null}

      {details.data ? (
        <div className="mt-4 rounded-lg bg-slate-900 p-4">
          <p className="text-caption text-chalk-faint">Requesting access</p>
          <p className="mt-1 text-body text-chalk">{details.data.label}</p>
          <p className="mt-2 text-caption text-chalk-dim">
            It will be able to record and upload to your library, and to your team's if you are
            on one. It cannot change your billing or remove anyone from a team.
          </p>
        </div>
      ) : null}

      {details.data?.alreadyHandled ? (
        <p className="mt-3 text-caption text-caution">
          That code has already been used. Ask the app for a new one.
        </p>
      ) : null}

      {decide.isError ? (
        <div className="mt-3">
          <ErrorText>{errorMessage(decide.error)}</ErrorText>
        </div>
      ) : null}

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button
          variant="primary"
          size="lg"
          className="sm:flex-1"
          busy={decide.isPending && decide.variables === 'approve'}
          disabled={!details.data || details.data.alreadyHandled}
          onClick={() => decide.mutate('approve')}
        >
          {details.data?.label.includes('Chrome') ? 'Connect this browser' : 'Connect this Mac'}
        </Button>
        <Button
          size="lg"
          className="sm:flex-1"
          busy={decide.isPending && decide.variables === 'deny'}
          disabled={!details.data || details.data.alreadyHandled}
          onClick={() => decide.mutate('deny')}
        >
          Not me
        </Button>
      </div>

      <p className="mt-4 text-caption text-chalk-faint">
        If you did not just open Slate for Mac, choose Not me. Nobody can connect a device without
        this code.
      </p>
    </Shell>
  );
}
